// src/pages/CreateDepartment.js

import React, { useState, useEffect } from 'react';
import axiosInstance from '../services/axiosInstance';
import GlobalTable from '../components/GlobalTable';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { handleApiError, handleApiSuccess } from "../components/apiErrorHandler";

const CreateDepartment = () => {
    const [department, setDepartment] = useState({
        department: '',
        organization: 'b835b027-72ba-4c99-af9c-c8c6c1e1c732',
        par_department: '', 
        dep_type: '',
        address: '',
        contact_phone: '',
    });
    const [departments, setDepartments] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [isReadOnly, setIsReadOnly] = useState(false);
    const [dialogName, setDialogName] = useState('Create Department');
    const [loading, setLoading] = useState(true);

    // Fetch departments and organizations
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true)
            try {
                const departmentResult = await axiosInstance.get('/api/departments/');
                setDepartments(departmentResult.data);

                const orgResult = await axiosInstance.get('/api/organizations/');
                setOrganizations(orgResult.data);
                setLoading(false)
            } catch (error) {
                handleApiError(error);
                setLoading(false)
            }
        };
        fetchData();
    }, []);

    // Handle form field changes
    const handleChange = (e, field) => {
        const value = e.target ? e.target.value : e.value;
        setDepartment((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    // Handle department creation
    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const result = await axiosInstance.post('/api/departments/', department);
            setDepartment({ department: '', organization: 'b835b027-72ba-4c99-af9c-c8c6c1e1c732', par_department: '', dep_type: '' });
            setDepartments([...departments, result.data]);
            handleApiSuccess(`Department created: ${result.data.department}`);
        } catch (error) {
            handleApiError(error);
        }
    };

    // Handle row edit completion
    const handleRowEditComplete = async (e) => {
        const { newData, index } = e;
        const payload = { ...newData, par_department: newData.par_department || null };
        try {
            const updatedDep = await axiosInstance.put(
                `/api/departments/${newData.id}/`,
                payload
            );
            const updatedDepartments = [...departments];
            updatedDepartments[index] = updatedDep.data;
            setDepartments(updatedDepartments);
            handleApiSuccess('Department updated successfully!');
        } catch (error) {
            handleApiError(error);
        }
    };

    // Open the dialog in read-only mode with data
    const handleRowClick = (dep) => {
        setDepartment(dep);
        setIsReadOnly(true);  // Set to read-only mode
        setDialogVisible(true);
        setDialogName('Department Details')
    };

    // Function to get organization name by id
    const getOrganizationName = React.useCallback((orgId) => {
        const organization = organizations.find(org => org.id === orgId);
        return organization ? organization.organization : 'N/A';
    }, [organizations]);

    // Function to get parent department name by id
    const getParDep = React.useCallback((depId) => {
        const pardep = departments.find(dep => dep.id === depId);
        return pardep ? pardep.department : '';
    }, [departments]);

    // Define columns and data for SortableTable
    const columns = React.useMemo(
        () => [
            {
                Header: 'Department Name',
                accessor: 'department',
                sortable: true, 
                filtered: true,
                bodyTemplate: (rowData) => (
                    <a
                        href="/departments/#"
                        onClick={(e) => {
                            e.preventDefault();
                            handleRowClick(rowData);
                        }}
                    >
                        {rowData.department}
                    </a>
                ),
            },
            {
                Header: 'Parent Department',
                accessor: 'par_department',
                sortable: true, 
                filtered: true,
                bodyTemplate: (rowData) => getParDep(rowData.par_department),
            },
            {
                Header: 'Department Type',
                accessor: 'dep_type',
                sortable: true, 
                filtered: true,
            },
            { Header: 'Address', accessor: 'address' },
            { Header: 'Phone', accessor: 'contact_phone' },
        ],
        [getParDep]
    );

    const data = React.useMemo(
        () => departments.map(dep => ({
            ...dep,
            organizationName: getOrganizationName(dep.organization),
            depName: getParDep(dep.par_department),
        })),
        [departments, getOrganizationName, getParDep]
    );

    const handleAddClick = () => {
        setDepartment({
            department: '',
            organization: 'b835b027-72ba-4c99-af9c-c8c6c1e1c732',
            par_department: '', 
            dep_type: '',
        });
        setIsReadOnly(false);
        setDialogName('Create Department')
        setDialogVisible(true);
    };

    const headerAction = (
        <Button type="button" icon="pi pi-plus" label=" Add Department" outlined onClick={handleAddClick} className="w-full sm:w-auto" />
    );

    return (
        <div>
            <div className='containers'>
                <GlobalTable
                    columns={columns}
                    data={data}
                    headerAction={headerAction}
                    tabletitle="Department List"
                    loading={loading}
                    customEditors={{
                        par_department: (options) => (
                            <Dropdown
                                value={options.value}
                                options={departments}
                                optionLabel="department"
                                optionValue="id"
                                onChange={(e) => options.editorCallback(e.value)}
                                placeholder="Select Parent Department"
                                showClear={true}
                            />
                        ),
                    }}
                    onRowEditComplete={handleRowEditComplete}
                />
            </div>

            <Dialog
                header={dialogName}
                visible={dialogVisible}
                onHide={() => setDialogVisible(false)}
                modal
                style={{ width: '32rem', borderRadius: '12px', padding: '.5rem'}} 
                breakpoints={{ '960px': '75vw', '641px': '90vw' }}
                className="p-fluid"
            >
                <form id="dialogForm" onSubmit={handleSubmit}>
                    <div style={{ borderRadius: '12px', padding: '1rem' }}>
                        <div className="field">
                            <label htmlFor="department" className="font-bold">Department Name:</label>
                            <InputText
                                name="department"
                                id="department"
                                value={department.department}
                                onChange={(e) => handleChange(e, 'department')}
                                placeholder="Enter Department name"
                                readOnly={isReadOnly}  // Set to read-only mode
                            />
                        </div>

                        <div className="field">
                            <label htmlFor='dep_type' className="font-bold">Department Type:</label>
                            <InputText
                                name="dep_type"
                                id="dep_type"
                                value={department.dep_type}
                                onChange={(e) => handleChange(e, 'dep_type')}
                                placeholder="Enter Department type"
                                readOnly={isReadOnly}  // Set to read-only mode
                            />
                        </div>

                        <div className="field">
                            <label htmlFor="par_department" className="font-bold">Parent Department:</label>
                            <Dropdown
                                id="par_department"
                                value={department.par_department}
                                options={departments}
                                optionLabel="department"
                                optionValue="id"
                                onChange={(e) => handleChange(e, 'par_department')}
                                placeholder="Select Parent Department"
                                disabled ={isReadOnly}  // Set to read-only mode
                            />
                        </div>
                        <div className="field">
                            <label>Address</label>
                            <InputText value={department.address} onChange={(e) => handleChange(e, 'address')} />
                        </div>
                        <div className="field">
                            <label>Phone</label>
                            <InputText value={department.contact_phone} onChange={(e) => handleChange(e, 'contact_phone')} />
                        </div>
                        </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                        <Button label="Save" icon="pi pi-check" form="dialogForm" type='submit' style={{borderRadius:"6px", maxWidth: "120px"}} disabled={isReadOnly} />
                        <Button label="Cancel" icon="pi pi-times" outlined onClick={(e) => {setDialogVisible(false); e.preventDefault();}}  style={{borderRadius:"6px", maxWidth: "120px"}} />
                    </div>  
                </form>
            </Dialog>
        </div>
    );
};

export default CreateDepartment;
